import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import BenchmarkScoreBar from '../BenchmarkScoreBar';
import BenchmarkChip from '../BenchmarkChip';
import BurnoutAlert from '../BurnoutAlert';
import ScoreBlock from '../ScoreBlock';
import { SkeletonLoader } from '../../../widgets/Skeletons';

import WidgetContainer from '../../WidgetContainer';
import Button from '../../../widgets/Button';
import Tooltip from '../../../widgets/Tooltip';

import { useDeviceType } from '../../../../utilities/common/deviceType';
import {
  BenchMarkScoreMetaData,
  WidgetDefinition,
} from '../../../../shared/types/widget';
import { WidgetType } from '../../../../shared/constants/widget';
import useTracking from '../../../../utilities/hooks/useTracking';
import {
  EventActions,
  EventNames,
} from '../../../../shared/constants/analytics';
import usePageName from '../../../../utilities/common/hooks/usePageName';

import helpIcon from '../../../../assets/icons/General/help-circle.svg';
import SearchIcon from '../../../../assets/icons/General/search-icon.svg';
import { Typography } from '../../../widgets/Typography';
import { ChevronBottomIcon } from '../../../../assets/icons/ChevronBottomIcon';
import { ChevronRightIcon } from '../../../../assets/icons/ChevronRightIcon';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';
import { CircleQuestionMark } from '../../../../assets/icons/CircleQuestionMark';
import TooltipV2 from '../../../widgets/ToolTipV2';
import { CircleInfoIcon } from '../../../../assets/icons/CircleInfo';

interface BenchmarkScoreProps {
  definition: WidgetDefinition;
  content: BenchMarkScoreMetaData;
}

function BenchmarkScore({ definition, content }: BenchmarkScoreProps) {
  const { title, subtitle } = definition;

  const { configData } = useFilterData();
  const isMobile = useDeviceType();

  const { trackEvent } = useTracking();
  const { pageName } = usePageName();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { uniqueRespondents } = content?.data || {};

  // Check if the value is higher or lower to adjust tooltip position
  const isSmallValue = [content?.data?.average, content?.data?.value].some(
    (v) => typeof v === 'number' && v <= 10,
  );
  const isLargeValue = [content?.data?.average, content?.data?.value].some(
    (v) => typeof v === 'number' && v >= 90,
  );

  const isEmptyState =
    content?.data?.value == null || content?.data?.value === 0;

  const checkWidgetHasMetUniqueRespondentsThreshold = () => {
    // If there is no minimum response threshold configured, always return true
    if (!configData?.minimumResponseThreshold || !uniqueRespondents) {
      return true;
    }

    return parseInt(uniqueRespondents) >= configData.minimumResponseThreshold;
  };

  const hasMetUniqueRespondentsThreshold =
    checkWidgetHasMetUniqueRespondentsThreshold();

  return (
    <div className="relative">
      <div className="divide-taupe-550 grid divide-y lg:flex lg:divide-x lg:divide-y-0">
        <div
          className={`col-span-4 flex flex-col px-6 py-8 md:max-w-[409px] ${isEmptyState ? 'min-w-75' : ''}`}
        >
          <div className="mb-2">
            {content?.meta?.dataAvailableFrom && (
              <div className="bg-grey-50 mb-3 flex w-fit items-center gap-x-2 rounded-full py-1 pr-3 pl-1">
                <BenchmarkChip
                  variant="small"
                  color="var(--color-grey-500)"
                  label={t('INFO')}
                />
                <span className="text-grey-600 text-xs">
                  {content?.meta?.dataAvailableFrom}
                </span>
              </div>
            )}
            <Typography
              as="h3"
              variant="display-xs"
              weight="bold"
              className="flex min-w-50 items-start gap-x-2"
            >
              {title}
              <Tooltip
                title={
                  <div className="text-sm">
                    <h6 className="text-grey-900 mb-1 font-semibold not-italic">
                      {title}
                    </h6>
                    <p className="text-grey-700 font-normal text-pretty">
                      {subtitle}
                    </p>
                  </div>
                }
                icon={<img src={helpIcon} alt="" />}
                position={`${isMobile ? 'bottom' : 'top'}`}
                color="bg-white"
                contentClass="!w-[328px] border border-black-200 font-normal shadow-[0_-12px_16px_-4px_rgba(16,24,40,0.08)] lg:shadow-[0_12px_16px_-4px_rgba(16,24,40,0.08)] !p-3 "
              />
            </Typography>
          </div>
          <Typography
            as="p"
            className={`text-grey-700 mb-4 overflow-hidden text-ellipsis ${content?.meta?.info ? 'line-clamp-6' : 'line-clamp-4'}`}
          >
            {content?.data?.description}
          </Typography>
          <Button
            className="group/benchmark-cta w-fit"
            variant="primary"
            data-html2canvas-ignore="true"
            onClick={() => {
              trackEvent(EventNames.WellBeingDashboard, {
                eventLabel: title,
                eventAction: EventActions.click,
                eventCategory: 'dimensions_details',
                eventSource: pageName,
                eventTab: '',
              });
              navigate(content?.meta?.link?.url);
            }}
            disabled={isEmptyState}
            rightIcon={
              <ChevronRightIcon
                className={`transition-transform group-hover/benchmark-cta:translate-x-1 ${isEmptyState ? 'stroke-grey-600' : 'stroke-white'}`}
              />
            }
          >
            {content?.meta?.link.label}
          </Button>
        </div>

        <div
          className={`relative col-span-8 flex w-full flex-col justify-center overflow-x-auto p-6 ${isSmallValue ? 'pl-14' : ''} ${isLargeValue ? 'pr-15' : ''}`}
        >
          {isEmptyState ? (
            <div className="text-center">
              <img
                src={SearchIcon}
                className="mx-auto mb-4"
                alt="Search icon"
              />
              <div>
                <p className="text-grey-700 mb-1 font-semibold">
                  {t('BENCHMARK_SCORE_EMPTY_STATE_HEADING')}
                </p>
                <p className="text-grey-500">
                  {t('BENCHMARK_SCORE_EMPTY_STATE_SUB_HEADING')}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid lg:min-w-fit">
              <div className="mr-8 mb-[3.25rem] flex flex-row items-center justify-between">
                <div className="text-grey-700 flex items-center gap-x-2 font-medium">
                  {t('YOUR_SCORE')}:
                  <ScoreBlock scoreData={content} />
                </div>
                <div className="flex flex-row items-center gap-x-1">
                  <Typography
                    variant="body-sm"
                    weight="medium"
                    className={
                      hasMetUniqueRespondentsThreshold
                        ? 'text-gray-800'
                        : 'text-red-500'
                    }
                  >
                    Unique respondents: {uniqueRespondents || 'N/A'}
                  </Typography>
                  {!hasMetUniqueRespondentsThreshold && (
                    <TooltipV2
                      wrapperClassName="border text-gray-800"
                      wrapperBackgroundColor="var(--color-red-25)"
                      wrapperBorderColor="var(--color-red-100)"
                      content={
                        <div className="flex flex-row">
                          <CircleInfoIcon className="mr-2 h-lh text-red-600" />
                          <div className="flex flex-col">
                            <Typography variant="body-sm" weight="semibold">
                              Additional responses needed for statistical
                              validity
                            </Typography>
                            <Typography
                              variant="body-sm"
                              className="text-grey-700 w-xs md:w-lg"
                            >
                              We need a minimum of 20 respondents for small
                              (headcount &lt;100), 40 respondents for medium
                              (headcount: 100-999) and 80 respondents for large
                              (headcount &gt;= 1000) organisations to maintain
                              statistical validity of reporting.
                            </Typography>
                          </div>
                        </div>
                      }
                    >
                      <CircleQuestionMark className="text-red-500" />
                    </TooltipV2>
                  )}
                </div>
              </div>
              <BenchmarkScoreBar scoreBarData={content} />
              {content?.meta?.info && (
                <div className="mt-6 w-full">
                  <BurnoutAlert info={content?.meta?.info} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BenchmarkScore;
